import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../context/api';

const Loader = () => (
  <div className="flex justify-center items-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const Students = () => {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
  }, [limit, offset]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/getallstudents`, {
        params: { limit, offset }
      });
      setStudents(response.data.students || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/search/${searchTerm}`);
      setStudents(response.data || []);
      setTotal(response.data.length || 0);
    } catch (error) {
      console.error('Error searching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSearch = async () => {
    setSearchTerm('');
    setOffset(0);
    fetchStudents();
  };

  const handleViewStudent = (regNumber) => {
    navigate(`/view-student/${regNumber}`);
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const handlePreviousPage = () => {
    if (offset - limit >= 0) {
      setOffset(offset - limit);
    }
  };

  const handleCreateStudent = () => {
    navigate('/create-student');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Students</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {/* Search and Actions Section */}
          <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Search students..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <button
                  className="flex-1 px-4 py-2 text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  onClick={handleResetSearch}
                >
                  Reset
                </button>
              </div>
              <div>
                <button
                  className="w-full px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                  onClick={handleCreateStudent}
                >
                  Add Student
                </button>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader />
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-pink-600 rounded-t-lg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Surname</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reg Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Gender</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student.RegNumber} className="hover:bg-pink-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.Name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.Surname}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.RegNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{student.Gender}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                          onClick={() => handleViewStudent(student.RegNumber)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Section */}
          <div className="mt-6 flex justify-between items-center">
            <button
              className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePreviousPage}
              disabled={offset === 0}
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {Math.ceil(offset / limit) + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleNextPage}
              disabled={offset + limit >= total}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Students;
